import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardHeader,
  Divider,
  CardContent,
  TextField,
  Grid,
  CardActions,
  Button,
  IconButton,
  MenuItem,
  ListItemText,
  Alert,
  Typography,
  Link
} from '@mui/material';
import BackIcon from '@mui/icons-material/ChevronLeft';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import {
  showAlert as showGlobalAlert,
  clearAllAlerts as clearAllGlobalAlerts
} from '../store/actions/alertActions';
import VehiclesService from '../services/VehiclesService';
import { formatErrorMessage, formatSuccessMessage, sortByKey } from '../utils/common';
import EnumService from '../services/EnumService';
import { EnumRes, VehicleIssueRes, VehicleResPage } from '../proxy/proxy';
import { hide, show } from '../store/actions/busyIndicatorActions';
import FormattedVehicle from '../models/FormattedVehicle';
import VehicleIssuesService from '../services/VehicleIssuesService';

interface Props {
    showAlert: (message: any) => any,
    clearAllAlerts: () => any,
    showBusyIndicator: () => any,
    hideBusyIndicator: () => any,
}

const VehicleIssueForm: React.FC<Props> = ({ showAlert, clearAllAlerts, showBusyIndicator, hideBusyIndicator }) => {
  const { id, shiftId } = useParams();
  const shiftAssciateId = !Number.isNaN(Number(shiftId)) ? Number(shiftId) : 0;
  const issueId = !Number.isNaN(Number(id)) ? Number(id) : 0;

  const [allVehicles, setAllVehicles] = useState<FormattedVehicle[]>([]);
  const [allVehicleIssueStates, setAllVehicleIssueStates] = useState<EnumRes[]>([]);
  const [issueData, setIssueData] = useState<VehicleIssueRes>();

  const [actionUpdating, setActionUpdating] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');

  const navigate = useNavigate();

  const back = () => {
    navigate(-1);
  };

  const handleFormSubmit = (values: any) => {
    showBusyIndicator();
    // clearAllAlerts();
    const payload = {
      shiftId: shiftAssciateId !== 0 ? shiftAssciateId : null,
      summary: values.summary,
      actionTaken: values.actionTaken,
      vehicleIssueStatus: Number(values.vehicleIssueStatus),
      vehicleId: Number(values.vehicleId)
    };

    if (issueId) {
      VehicleIssuesService.updateVehicleIssue(issueId, payload)
        .then((response) => {
          hideBusyIndicator();
          showAlert(formatSuccessMessage('Vehicle issue updated successfully'));
          back();
        })
        .catch((err) => {
          showAlert(formatErrorMessage(JSON.parse(err.response)));
        });
    } else {
      VehicleIssuesService.createVehicleIssue(payload)
        .then((response) => {
          hideBusyIndicator();
          showAlert(formatSuccessMessage('Vehicle issue reported successfully'));
          back();
        })
        .catch((err) => {
          hideBusyIndicator();
          showAlert(formatErrorMessage(JSON.parse(err.response)));
        });
    }
  };

  const updateAction = () => {
    const payload = {
      vehicleIssueStatus: issueData?.vehicleIssueStatus,
      shiftId: issueData?.shift?.id,
      summary: issueData?.summary || '',
      actionTaken: action,
      vehicleId: issueData?.vehicle?.id
    };

    VehicleIssuesService.updateVehicleIssue(issueId, payload)
      .then((response) => {
        hideBusyIndicator();
        setActionUpdating(false);
        showAlert(formatSuccessMessage('Action taken updated successfully'));
      })
      .catch((err) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const refreshData = () => {
    const promises: Array<Promise<any>> = [
      EnumService.getEnums('vehicleIssueStatuses'),
      VehiclesService.getAllVehicles(),
      ...(issueId ? [VehicleIssuesService.getVehicleIssue(issueId)] : [])
    ];

    showBusyIndicator();
    clearAllAlerts();

    Promise.all(promises)
      .then((result) => {
        setAllVehicleIssueStates(sortByKey(result[0], 'value'));
        setAllVehicles(result[1]);
        if (issueId) {
          setIssueData(result[2]);
          setAction(result[2].actionTaken);
        } else {
          setIssueData({});
        }
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  useEffect(() => {
    refreshData();
  }, []);

  const cancelActionUpdate = () => {
    setActionUpdating(false);
    setAction(issueData?.actionTaken || '');
  };

  const startActionUpdate = () => {
    setActionUpdating(true);
    setAction(issueData?.actionTaken || '');
  };

  return (
    <Box
      sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 3 }}
    >
      <Container maxWidth="xl">
        <Card>
          <CardHeader
            avatar={(
              <IconButton aria-label="back" size="small" onClick={back}>
                <BackIcon />
              </IconButton>
            )}
            title="Vehicle Issue Details"
          />
          <Divider />
          <CardContent>
            { shiftAssciateId !== 0
              && (<Alert sx={{ margin: 1 }} key={shiftAssciateId} severity="warning">Issue will be associated with the shift</Alert>)
            }
            { issueData && (
              <Formik
                initialValues={{
                  summary: issueData?.summary || '',
                  actionTaken: issueData?.actionTaken || '',
                  vehicleIssueStatus: issueData?.vehicleIssueStatus || 0,
                  vehicleId: issueData?.vehicle?.id || 0
                }}
                validationSchema={Yup.object().shape({
                  summary: Yup.string().required('Issue is required'),
                  actionTaken: Yup.string().required('Action taken is required'),
                  vehicleIssueStatus: Yup.number()
                    .min(0)
                    .required('Vehicle issue status is required'),
                  vehicleId: Yup.number().required('Vehicle ID is required')
                })}
                onSubmit={handleFormSubmit}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  isValid,
                  dirty
                }) => (
                  <form onSubmit={handleSubmit}>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            error={Boolean(touched.vehicleId && errors.vehicleId)}
                            fullWidth
                            helperText={touched.vehicleId && errors.vehicleId}
                            label="Vehicle"
                            margin="normal"
                            name="vehicleId"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.vehicleId}
                            variant="outlined"
                            required
                            select
                          >
                            <MenuItem aria-label="None" value="">
                              <ListItemText primary="&nbsp;" />
                            </MenuItem>
                            { allVehicles.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                <ListItemText primary={option.registrationNumber} />
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            error={Boolean(
                              touched.vehicleIssueStatus
                              && errors.vehicleIssueStatus
                            )}
                            fullWidth
                            helperText={
                              touched.vehicleIssueStatus
                              && errors.vehicleIssueStatus
                            }
                            label="Vehicle Issue Status"
                            margin="normal"
                            name="vehicleIssueStatus"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.vehicleIssueStatus}
                            variant="outlined"
                            disabled={issueId === 0}
                            required
                            select
                          >
                            <MenuItem aria-label="None" value="">
                              <ListItemText primary="&nbsp;" />
                            </MenuItem>
                            { allVehicleIssueStates.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                <ListItemText primary={option.value} />
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            error={Boolean(touched.summary && errors.summary)}
                            fullWidth
                            helperText={touched.summary && errors.summary}
                            label="Summary"
                            margin="normal"
                            name="summary"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.summary}
                            variant="outlined"
                            required
                          />
                        </Grid>
                        {
                          issueId > 0 && (
                            <>
                              <Grid item xs={12}>
                                <Typography variant="body1">
                                  <b>Action Taken:</b>&nbsp;&nbsp;
                                  {
                                    actionUpdating ? (
                                      <>
                                        <Link
                                          component="button"
                                          disabled={(issueData?.actionTaken ?? '') === action}
                                          variant="body1"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            updateAction();
                                          }}
                                        >
                                          Save
                                        </Link>&nbsp;&nbsp;
                                        <Link
                                          component="button"
                                          variant="body1"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            cancelActionUpdate();
                                          }}
                                        >
                                          Cancel
                                        </Link>
                                      </>
                                    ) : (
                                      <Link component="button" variant="body1" onClick={startActionUpdate}>Update</Link>
                                    )
                                  }
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                {
                                  actionUpdating ? (
                                    <TextField multiline fullWidth value={action} onChange={(event) => setAction(event.target.value)} />
                                  ) : (
                                    <pre style={{ whiteSpace: 'break-spaces', fontSize: 16 }}>{action || '--'}</pre>
                                  )
                                }
                              </Grid>
                            </>
                          )
                        }
                        {
                          !(issueId > 0) && (
                            <Grid item xs={12}>
                              <TextField
                                error={Boolean(
                                  touched.actionTaken && errors.actionTaken
                                )}
                                fullWidth
                                helperText={touched.actionTaken && errors.actionTaken}
                                label="Action Taken"
                                margin="normal"
                                name="actionTaken"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.actionTaken}
                                variant="outlined"
                                required
                              />
                            </Grid>
                          )
                        }
                      </Grid>
                    </CardContent>
                    <CardActions
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <Button
                        sx={{ marginRight: 2 }}
                        color="secondary"
                        onClick={() => navigate(-1)}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!dirty || !isValid}
                      >
                        Submit
                      </Button>
                    </CardActions>
                  </form>
                )}
              </Formik>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleIssueForm);
